import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData, logout } from "../../../services/apiService";
import {
  changeAccessCode,
  changePassword,
} from "../../../services/credentialPolicyService";
import {
  PASSWORD_PATTERN,
  shouldSkipOldCredentialValidation,
} from "../../../utils/credentialGateUtils";
import {
  CredentialAccessCodeRequirements,
  CredentialModalTabs,
  CredentialPasswordField,
  CredentialPasswordRequirements,
} from "./credentialChangeModalParts";
import "./ChangeCredentialsModal.scss";
import "./RequiredCredentialChange.scss";

const RequiredCredentialChange = ({ forced = true }) => {
  const navigate = useNavigate();
  const userData = getUserData();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [accessCodeSaved, setAccessCodeSaved] = useState(false);
  const [tab, setTab] = useState("password");

  const skipOldValidation = shouldSkipOldCredentialValidation(userData);

  const [pwdForm, setPwdForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [codeForm, setCodeForm] = useState({
    newAccessCode: "",
    confirmAccessCode: "",
  });

  const title = useMemo(() => {
    if (userData?.PasswordExpired || userData?.AccessCodeExpired) {
      return "Your credentials have expired";
    }
    return "Change your login credentials";
  }, [userData]);

  const signOutAfterBothSaved = () => {
    setSuccess(
      "Password and access code updated. Signing you out so you can sign in with the new credentials.",
    );
    setSigningOut(true);
    window.setTimeout(() => logout(navigate), 900);
  };

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (tab === "password") {
      if (!PASSWORD_PATTERN.test(pwdForm.newPassword)) {
        setError("Please meet all password requirements before continuing.");
        return;
      }
      if (pwdForm.newPassword !== pwdForm.confirmPassword) {
        setError("New password and confirm password do not match.");
        return;
      }

      setLoading(true);
      try {
        const response = await changePassword({
          StaffIdRef: userData.StaffId,
          OrganizationIdRef: userData.OrganizationId,
          OldPassword: "",
          NewPassword: pwdForm.newPassword,
          ConfirmPassword: pwdForm.confirmPassword,
          SkipOldValidation: skipOldValidation,
        });
        if (response.success !== 1) {
          setError(response.message || "Could not change password.");
          return;
        }
        setPwdForm({ newPassword: "", confirmPassword: "" });
        setPasswordSaved(true);
        if (accessCodeSaved) {
          signOutAfterBothSaved();
        } else {
          setSuccess("Password updated. Please set a new access code.");
          setTab("accessCode");
        }
      } catch (err) {
        setError(err?.message || "Could not change password.");
      } finally {
        setLoading(false);
      }
      return;
    }

    const next = codeForm.newAccessCode.trim();
    const confirm = codeForm.confirmAccessCode.trim();
    if (next.length !== 6 || !/^\d{6}$/.test(next)) {
      setError("Please meet all access code requirements before continuing.");
      return;
    }
    if (next !== confirm) {
      setError("New access code and confirm access code do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await changeAccessCode({
        StaffIdRef: userData.StaffId,
        OrganizationIdRef: userData.OrganizationId,
        OldAccessCode: 0,
        NewAccessCode: parseInt(codeForm.newAccessCode, 10),
        ConfirmAccessCode: parseInt(codeForm.confirmAccessCode, 10),
        SkipOldValidation: skipOldValidation,
      });
      if (response.success !== 1) {
        setError(response.message || "Could not change access code.");
        return;
      }
      setCodeForm({ newAccessCode: "", confirmAccessCode: "" });
      setAccessCodeSaved(true);
      if (passwordSaved) {
        signOutAfterBothSaved();
      } else {
        setSuccess("Access code updated. Please set a new password.");
        setTab("password");
      }
    } catch (err) {
      setError(err?.message || "Could not change access code.");
    } finally {
      setLoading(false);
    }
  };

  const showPasswordForm = tab === "password";

  return (
    <div className="required-cred-page">
      <div className="required-cred-card credential-admin-modal">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{title}</h5>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                {forced
                  ? "You must set a new password and a new access code before you can enter an organisation. When both are saved you will be signed out and must sign in again."
                  : "Update your password and access code."}
              </p>

              <CredentialModalTabs tab={tab} onTabChange={handleTabChange} />

              {error && (
                <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success py-2 px-3 small mb-3" role="alert">
                  {success}
                </div>
              )}

              {showPasswordForm ? (
                <>
                  <CredentialPasswordRequirements password={pwdForm.newPassword} />
                  <CredentialPasswordField
                    id="required-new-password"
                    label="New password"
                    value={pwdForm.newPassword}
                    autoComplete="new-password"
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, newPassword: e.target.value }))
                    }
                  />
                  <CredentialPasswordField
                    id="required-confirm-password"
                    label="Confirm new password"
                    value={pwdForm.confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, confirmPassword: e.target.value }))
                    }
                  />
                </>
              ) : (
                <>
                  <CredentialAccessCodeRequirements
                    newAccessCode={codeForm.newAccessCode}
                    confirmAccessCode={codeForm.confirmAccessCode}
                  />
                  <CredentialPasswordField
                    id="required-new-access-code"
                    label="New access code"
                    value={codeForm.newAccessCode}
                    maxLength={6}
                    autoComplete="off"
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        newAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                  <CredentialPasswordField
                    id="required-confirm-access-code"
                    label="Confirm new access code"
                    value={codeForm.confirmAccessCode}
                    maxLength={6}
                    autoComplete="off"
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        confirmAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="submit"
                className="btn btn-primary ms-auto"
                disabled={loading || signingOut}
              >
                <i className="fas fa-check me-1" aria-hidden="true" />
                {loading
                  ? "Saving…"
                  : showPasswordForm
                    ? "Update password"
                    : "Update access code"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequiredCredentialChange;
